import React from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import TopBarCenter from "@/sot/primitives/TopBarCenter.jsx";
import Button from "@/components/ui/Button.jsx";
import Card from "@/components/ui/Card.jsx";
import Checkbox from "@/components/ui/Checkbox.jsx";
import {
  CheckoutPaymentMethodRow,
  PaymentDetailsHeader,
  TestPaymentHelper,
} from "@/components/payments/CheckoutPaymentDetails.jsx";
import ContextFeedbackLauncher from "@/components/feedback/ContextFeedbackLauncher.jsx";
import { useAuth } from "@/contexts/AuthContext.jsx";
import { useLanguage } from "@/contexts/LanguageContext.jsx";
import supabase from "@/services/supabaseClient";
import {
  initializePaystackPharmacyPayment,
  verifyPaystackPharmacyPayment,
} from "@/lib/payments/api.ts";
import {
  clearPendingSponsorPayment,
  readPendingSponsorPayment,
} from "@/lib/payments/sponsorPaymentRecovery.js";
import { resolveValidatedSession } from "@/lib/authSession.js";
import { openPaystackInlineCheckout } from "@/lib/payments/paystackInline.js";
import {
  activePaymentProviderMode,
  isPaystackRuntime,
  isPaystackTestRuntime,
  isWorldlineRuntime,
} from "@/lib/payments/providerRuntime.js";
import { resolvePaymentCurrency } from "@/services/marketPaymentService.js";
import { formatCurrencyFromCents } from "@/utils/formatCurrency.js";

function normalizeText(value) {
  return String(value || "").trim();
}

function formatCheckoutError(error, t) {
  if (error?.body?.code === "clinician_payout_not_ready") {
    return String(error?.body?.payment_unavailable_message || "").trim() ||
      t(
        "prescriptions.checkout.errors.clinicianPayoutPending",
        "Payment is not available yet because this clinician's Stripe payout account is still being verified. You can continue testing the booking flow, or choose another payment option if available.",
      );
  }
  const raw = String(error?.message || error || "").trim();
  const lower = raw.toLowerCase();

  if (
    lower.includes("clinician_payout_not_ready") ||
    lower.includes("payout account is still being verified") ||
    lower.includes("stripe activates transfers")
  ) {
    return t(
      "prescriptions.checkout.errors.clinicianPayoutPending",
      "Payment is not available yet because this clinician's Stripe payout account is still being verified. You can continue testing the booking flow, or choose another payment option if available.",
    );
  }

  if (lower.includes("no such payment_intent")) {
    return t(
      "prescriptions.checkout.errors.intentMissing",
      "Stripe could not find this pharmacy payment intent in the current browser context. Refresh payment details and try again.",
    );
  }

  if (
    lower.includes("no such destination") ||
    lower.includes("pharmacy payout account must be reconnected") ||
    lower.includes("pharmacy_payout_reconnect_required")
  ) {
    return t(
      "prescriptions.checkout.errors.payoutReconnect",
      "Pharmacy payout setup must be reconnected before card checkout can continue.",
    );
  }

  if (
    lower.includes("client secret") ||
    lower.includes("client_secret") ||
    lower.includes("does not match any associated paymentintent")
  ) {
    return t(
      "prescriptions.checkout.errors.secureCheckout",
      "Secure checkout could not be prepared cleanly. Refresh payment details and try again.",
    );
  }

  if (lower.includes("processing")) {
    return t(
      "prescriptions.checkout.errors.processing",
      "Your payment is still processing. Please wait a moment and try again.",
    );
  }

  return (
    raw ||
    t(
      "prescriptions.checkout.errors.generic",
      "Payment may have gone through, but AfriMed™ could not finish the pharmacy checkout cleanly.",
    )
  );
}

function buildPaymentIntentMismatchMessage(platformAccountId = "", t) {
  const accountLabel = normalizeText(platformAccountId);
  return accountLabel
    ? t(
        "prescriptions.checkout.errors.intentMismatchWithAccount",
        "Stripe could not find this pharmacy payment intent in the active card form context. AfriMed™ created it in platform account {account}. Refresh payment details and try again.",
        { account: accountLabel },
      )
    : t(
        "prescriptions.checkout.errors.intentMismatch",
        "Stripe could not find this pharmacy payment intent in the active card form context. Refresh payment details and try again.",
      );
}

function formatFulfillmentModeLabel(value = "", t) {
  return normalizeText(value).toLowerCase() === "delivery"
    ? t("prescriptions.fulfillment.modeDelivery", "Delivery")
    : t("prescriptions.fulfillment.modePickup", "Pickup");
}

function buildCheckoutStatusSummary(paymentView, t) {
  if (paymentView.paymentStatus === "paid") {
    return {
      label: t("prescriptions.checkout.state.paid", "Paid"),
      helper: t(
        "prescriptions.checkout.state.paidHelper",
        "Return to the prescription details for release and pickup updates.",
      ),
    };
  }

  if (paymentView.paymentStatus === "refund_pending" || paymentView.refundStatus === "pending") {
    return {
      label: t("prescriptions.checkout.state.refundPending", "Refund pending"),
      helper: t(
        "prescriptions.checkout.state.refundPendingHelper",
        "This pharmacy payment is being reversed.",
      ),
    };
  }

  if (paymentView.paymentStatus === "refunded" || paymentView.refundStatus === "succeeded") {
    return {
      label: t("prescriptions.checkout.state.refunded", "Refunded"),
      helper: t(
        "prescriptions.checkout.state.refundedHelper",
        "Choose another pharmacy if this prescription still needs fulfilment.",
      ),
    };
  }

  if (paymentView.deliveryQuoteIncomplete) {
    return {
      label: t("prescriptions.checkout.state.deliveryQuoteIncomplete", "Delivery quote incomplete"),
      helper: t("prescriptions.checkout.state.deliveryQuoteIncompleteHelper", "The pharmacy is still finalising your delivery quote."),
    };
  }

  if (paymentView.quoteReady) {
    return {
      label: t("prescriptions.checkout.state.paymentReady", "Payment ready"),
      helper: t(
        "prescriptions.checkout.state.paymentReadyHelper",
        "The pharmacy prepared a quote. Complete payment to confirm the order.",
      ),
    };
  }

  return {
    label: t("prescriptions.checkout.state.awaitingQuote", "Awaiting quote"),
    helper: t(
      "prescriptions.checkout.state.awaitingQuoteHelper",
      "Return to the prescription details once the pharmacy prepares pricing.",
    ),
  };
}

function resolvePrescriptionPaymentView(row = {}, patientName = "", t) {
  const meta = row?.meta && typeof row.meta === "object" ? row.meta : {};
  const currency = resolvePaymentCurrency({
    explicitCurrency: meta?.currency,
    sponsorCountry:
      meta?.sponsor_country ||
      meta?.sponsorCountry ||
      meta?.payer_market ||
      meta?.payerMarket,
    sponsorDialCode: meta?.sponsor_dial_code || meta?.sponsorDialCode,
    country:
      meta?.pharmacy_country ||
      meta?.pharmacyCountry ||
      meta?.patient_country ||
      meta?.patientCountry,
    fallbackCurrency: "ZAR",
  });
  const medicineSubtotalCents = Number(meta?.medicine_subtotal_cents || 0);
  const deliveryFeeCents = Number(meta?.delivery_fee_cents || 0);
  const paymentStatus = normalizeText(meta?.payment_status).toLowerCase() || "pending";
  const refundStatus = normalizeText(meta?.refund_status || meta?.stripe_refund_status).toLowerCase();
  const fulfillmentMode = normalizeText(meta?.fulfillment_mode || "pickup").toLowerCase() || "pickup";
  const deliveryQuoteIncomplete =
    fulfillmentMode === "delivery" && paymentStatus !== "paid" && meta?.delivery_quote_confirmed !== true;

  return {
    prescriptionId: normalizeText(row?.id),
    rxNo:
      normalizeText(meta?.rxNo || row?.external_rx_id) ||
      `RX-${String(row?.id || "").slice(0, 8).toUpperCase()}`,
    medicineName:
      normalizeText(
        meta?.medicineName || meta?.medicine_name || meta?.medicationName || meta?.medicine,
      ) || t("prescriptions.common.medication", "Medication"),
    form: normalizeText(meta?.form || meta?.strengthLabel || meta?.strength_label),
    patientName:
      patientName ||
      normalizeText(
        meta?.patientName || meta?.patient_name || meta?.patientFullName || meta?.patient_full_name,
      ),
    doctorName: normalizeText(
      meta?.doctor_name || meta?.doctorName || meta?.doctorFullName || meta?.doctor_full_name,
    ),
    pharmacyName: normalizeText(meta?.pharmacy_branch_name || meta?.pharmacy_name || meta?.pharmacyName),
    pharmacyOwnerUserId: normalizeText(meta?.pharmacy_owner_user_id),
    supportConversationId: normalizeText(meta?.fulfillment_support_conversation_id),
    fulfillmentMode,
    deliveryQuoteIncomplete,
    medicineSubtotalCents: Number.isFinite(medicineSubtotalCents) ? medicineSubtotalCents : 0,
    deliveryFeeCents: Number.isFinite(deliveryFeeCents) ? deliveryFeeCents : 0,
    totalCents:
      (Number.isFinite(medicineSubtotalCents) ? medicineSubtotalCents : 0) +
      (Number.isFinite(deliveryFeeCents) ? deliveryFeeCents : 0),
    paymentStatus,
    refundStatus,
    currency,
    quoteReady:
      !!normalizeText(meta?.pharmacy_owner_user_id) &&
      Number.isFinite(medicineSubtotalCents) &&
      medicineSubtotalCents > 0 && !deliveryQuoteIncomplete,
  };
}

function CheckoutSummaryItem({ label, value, helper = "", emphasis = false }) {
  return (
    <div className="min-w-0">
      <div className="text-label-large text-on-surface-var">{label}</div>
      <div className={`mt-1 break-words ${emphasis ? "text-title-large" : "text-title-medium"} text-on-surface`}>{value}</div>
      {helper ? <div className="mt-1 text-body-small text-on-surface-var">{helper}</div> : null}
    </div>
  );
}

function StatusCard({ children, tone = "default" }) {
  const toneClass =
    tone === "critical"
      ? "bg-[var(--state-critical-08)] text-[rgb(var(--schemes-critical))]"
      : tone === "success"
        ? "bg-[rgba(0,189,196,0.12)] text-[#008A8F]"
        : "text-on-surface-var";
  return <Card className={`px-5 py-5 text-body-medium ${toneClass}`}>{children}</Card>;
}

function formatSavedCardLabel(method = {}, t) {
  const brand = normalizeText(method?.brand) || t("prescriptions.checkout.card.brandFallback", "Card");
  const last4 = normalizeText(method?.last4);
  const month = Number(method?.expMonth || 0);
  const year = Number(method?.expYear || 0);
  const expiry = month && year ? ` · ${String(month).padStart(2, "0")}/${String(year).slice(-2)}` : "";
  return last4 ? `${brand} ending ${last4}${expiry}` : brand;
}

function PaystackTestCheckout({ onPay, onPrepareSponsorPayment, submitting, error, showTestHelper = false }) {
  const [selectedMethod, setSelectedMethod] = React.useState("card");
  return (
    <section className="min-w-0">
      <PaymentDetailsHeader />
      <div className="mt-4 grid gap-3">
        <CheckoutPaymentMethodRow
          title="Card"
          helper="Pay securely with a saved or new card."
          selected={selectedMethod === "card"}
          onClick={() => setSelectedMethod("card")}
        />
        <CheckoutPaymentMethodRow
          title="Sponsor payment"
          helper="Send a secure payment link to a family member, guardian, or sponsor."
          selected={selectedMethod === "sponsor"}
          onClick={() => setSelectedMethod("sponsor")}
        />
      </div>
      {selectedMethod === "sponsor" ? (
        <Card className="mt-4 p-4">
          <div className="text-title-medium text-on-surface">Sponsor payment</div>
          <div className="mt-2 text-body-medium text-on-surface-var">Your pharmacy quote stays in place while the sponsor completes payment securely.</div>
          <Button type="button" variant="tonal" className="mt-4 h-12" onClick={onPrepareSponsorPayment} disabled={submitting}>Prepare sponsor payment</Button>
        </Card>
      ) : (
        <>
          {showTestHelper ? <div className="mt-4"><TestPaymentHelper onRunTest={onPay} running={submitting} /></div> : null}
          {error ? <div className="mt-4 rounded-[12px] bg-[var(--state-critical-08)] px-4 py-3 text-body-medium text-[rgb(var(--schemes-critical))]">We couldn’t start your secure payment. Please try again.</div> : null}
          {!showTestHelper ? <div className="mt-4 flex flex-wrap gap-3">
            <Button type="button" className="h-12" onClick={onPay} disabled={submitting}>{submitting ? "Opening secure payment…" : "Continue to secure payment"}</Button>
          </div> : null}
        </>
      )}
    </section>
  );
}

function CardCheckoutForm({
  rememberCard = false,
  onRememberCardChange,
  onRetry,
  onSubmit,
  onPrepareSponsorPayment,
  loadError = "",
  submitting = false,
  savedPaymentMethods = [],
  savedPaymentMethodsLoading = false,
  selectedSavedPaymentMethodId = "",
  useSavedPaymentMethod = false,
  onSelectSavedPaymentMethod,
  onUseNewCard,
}) {
  const { t } = useLanguage();
  const stripe = useStripe();
  const elements = useElements();
  const [elementReady, setElementReady] = React.useState(false);
  const [elementError, setElementError] = React.useState("");
  const [selectedMethod, setSelectedMethod] = React.useState("card");
  const hasSavedPaymentMethods = Array.isArray(savedPaymentMethods) && savedPaymentMethods.length > 0;
  const shouldRenderCardElement = selectedMethod === "card" && !useSavedPaymentMethod;

  React.useEffect(() => {
    setElementReady(false);
    setElementError("");
  }, [loadError, shouldRenderCardElement]);

  const activeLoadError = loadError || elementError;

  async function handlePayNow() {
    if (submitting || !stripe || !elements || (shouldRenderCardElement && !elementReady)) return;
    await onSubmit?.({ stripe, elements, savedPaymentMethodId: useSavedPaymentMethod ? selectedSavedPaymentMethodId : "" });
  }

  return (
    <section className="min-w-0">
      <div className="text-label-large text-primary">{t("prescriptions.checkout.card.title", "Payment details")}</div>
      <div className="mt-2 text-title-large text-on-surface">{t("prescriptions.checkout.chooseMethod", "How would you like to pay?")}</div>

      <div className="mt-4 grid gap-3">
        <PaymentMethodRow title={t("prescriptions.checkout.card.method", "Card")} helper={t("prescriptions.checkout.card.methodHelp", "Pay securely with a saved or new card.")} selected={selectedMethod === "card"} onClick={() => setSelectedMethod("card")} />
        <PaymentMethodRow title={t("prescriptions.checkout.sponsor.title", "Sponsor payment")} helper={t("prescriptions.checkout.sponsor.methodHelp", "Send a secure payment link to a family member, guardian, or sponsor.")} selected={selectedMethod === "sponsor"} onClick={() => setSelectedMethod("sponsor")} />
      </div>

      {selectedMethod === "sponsor" ? (
        <Card className="mt-4 p-4">
          <div className="text-title-medium text-on-surface">{t("prescriptions.checkout.sponsor.headline", "Use a secure sponsor link")}</div>
          <p className="mt-2 text-body-medium text-on-surface-var">{t("prescriptions.checkout.sponsor.body", "Send a secure payment link for this priced prescription. AfriMed™ will return the payment to this pharmacy order automatically.")}</p>
          <Button type="button" variant="tonal" className="mt-4 h-12" onClick={onPrepareSponsorPayment} disabled={submitting}>{t("prescriptions.checkout.sponsor.prepare", "Prepare sponsor payment")}</Button>
        </Card>
      ) : (
        <>

      {activeLoadError ? (
        <div className="mt-4 rounded-[12px] bg-[var(--state-critical-08)] px-4 py-3 text-body-medium text-[rgb(var(--schemes-critical))]">
          {activeLoadError}
        </div>
      ) : null}

      {hasSavedPaymentMethods ? <div className="mt-4 grid gap-3"><div className="text-title-small text-on-surface-var">{t("prescriptions.checkout.savedCards", "Saved cards")}</div>{savedPaymentMethods.map((method) => { const selected = useSavedPaymentMethod && selectedSavedPaymentMethodId === method.id; return <button key={method.id} type="button" onClick={() => onSelectSavedPaymentMethod?.(method.id)} className={`min-h-[76px] rounded-[12px] border px-4 py-3 text-left ${selected ? "border-[rgba(var(--afrimed-primary),0.72)] bg-[rgb(var(--clr-surface-cont-highest))] shadow-[var(--elev-1)]" : "border-[rgb(var(--clr-outline-variant))] bg-[rgb(var(--clr-surface))]"}`}><div className="text-title-medium text-on-surface">{formatSavedCardLabel(method, t)}</div><div className="mt-1 text-body-small text-on-surface-var">{t("prescriptions.checkout.savedCardHelp", "Use this saved card without typing the number again.")}</div></button>; })}<Button type="button" variant="tonal" className="h-11 w-fit" onClick={onUseNewCard} disabled={submitting}>{t("prescriptions.checkout.useDifferentCard", "Use a different card")}</Button></div> : null}
      {savedPaymentMethodsLoading ? <div className="mt-4 text-body-small text-on-surface-var">{t("prescriptions.checkout.loadingSavedCards", "Loading your saved cards...")}</div> : null}

      {shouldRenderCardElement ? <Card className="mt-4 p-4">
        {!elementReady && !activeLoadError ? (
          <div className="mb-3 text-body-small text-on-surface-var">
            {t("prescriptions.checkout.card.loading", "Loading secure card form…")}
          </div>
        ) : null}
        <CardElement
          onReady={() => {
            setElementReady(true);
            setElementError("");
          }}
          onChange={(event) => {
            if (event?.error?.message) {
              setElementError(formatCheckoutError(event.error, t));
              return;
            }
            setElementError("");
          }}
          options={{
            hidePostalCode: true,
            style: {
              base: {
                color: "#f8fafc",
                fontFamily: "Inter, ui-sans-serif, system-ui, sans-serif",
                fontSize: "16px",
                "::placeholder": {
                  color: "#9ea9bd",
                },
                iconColor: "#9ea9bd",
              },
              invalid: {
                color: "#f8533a",
                iconColor: "#f8533a",
              },
            },
          }}
        />
      </Card> : null}

      {shouldRenderCardElement ? <label className="mt-4 flex items-center gap-3 text-body-medium text-on-surface-var">
        <Checkbox checked={rememberCard} onChange={onRememberCardChange} />
        <span>{t("prescriptions.checkout.card.remember", "Remember credit card details for next time")}</span>
      </label> : null}

      <div className="mt-5 grid gap-3 sm:grid-cols-[minmax(0,1fr)_220px]">
        <Button
          type="button"
          className="h-12"
          disabled={submitting || !stripe || !elements || (shouldRenderCardElement && !elementReady) || !!activeLoadError || (!shouldRenderCardElement && !selectedSavedPaymentMethodId)}
          onClick={handlePayNow}
        >
          {submitting
            ? t("prescriptions.checkout.card.processing", "Processing…")
            : t("prescriptions.checkout.card.payNow", "Pay pharmacy now")}
        </Button>

        <Button
          type="button"
          variant="ghost"
          className="h-12"
          disabled={submitting}
          onClick={onRetry}
        >
          {t("prescriptions.checkout.card.refresh", "Refresh payment details")}
        </Button>
      </div>
        </>
      )}
    </section>
  );
}

export default function PatientPrescriptionPayment() {
  const location = useLocation();
  const nav = useNavigate();
  const { id } = useParams();
  const { user, profile } = useAuth();
  const { t } = useLanguage();
  const sponsorReturnParams = React.useMemo(
    () => new URLSearchParams(location.search || ""),
    [location.search],
  );
  const [rxState, setRxState] = React.useState({
    loading: true,
    error: "",
    row: null,
  });
  const [submitting, setSubmitting] = React.useState(false);
  const [submitError, setSubmitError] = React.useState("");
  const [sponsorReturnState, setSponsorReturnState] = React.useState({
    loading: false,
    error: "",
  });
  const paymentProviderMode = activePaymentProviderMode();
  const isPaystackProvider = isPaystackRuntime();
  const isPaystackTest = isPaystackTestRuntime();
  const isWorldlineProvider = isWorldlineRuntime();

  React.useEffect(() => {
    let alive = true;

    async function loadPrescription() {
      setRxState({ loading: true, error: "", row: null });

      try {
        const currentUserId = normalizeText(user?.id);
        if (!currentUserId) {
          throw new Error(t("prescriptions.checkout.errors.signInAgain", "Please sign in again to continue."));
        }

        const { data: prescription, error: prescriptionError } = await supabase
          .from("prescriptions")
          .select("id,appointment_id,patient_user_id,doctor_user_id,external_rx_id,meta")
          .eq("id", id)
          .maybeSingle();

        if (prescriptionError) throw prescriptionError;
        if (!prescription?.id) throw new Error(t("prescriptions.common.notFound", "Prescription not found."));

        const patientRef = normalizeText(prescription.patient_user_id);
        let allowed = patientRef === currentUserId;

        if (!allowed) {
          const { data: patientRow, error: patientError } = await supabase
            .from("patients")
            .select("id,user_id")
            .eq("user_id", currentUserId)
            .maybeSingle();

          if (patientError) throw patientError;

          allowed =
            patientRef === normalizeText(patientRow?.id) ||
            patientRef === normalizeText(patientRow?.user_id);
        }

        if (!allowed) {
          throw new Error(
            t("prescriptions.checkout.errors.accessDenied", "You do not have access to this prescription payment."),
          );
        }

        if (!alive) return;
        setRxState({ loading: false, error: "", row: prescription });
      } catch (error) {
        if (!alive) return;
        setRxState({
          loading: false,
          error:
            error?.message ||
            t("prescriptions.checkout.errors.load", "Could not load this prescription payment right now."),
          row: null,
        });
      }
    }

    void loadPrescription();
    return () => {
      alive = false;
    };
  }, [id, user?.id]);

  const paymentView = React.useMemo(
    () =>
      resolvePrescriptionPaymentView(
        rxState.row,
        normalizeText(
          profile?.display_name || profile?.full_name || [profile?.first_name, profile?.last_name].filter(Boolean).join(" "),
        ),
        t,
      ),
    [profile?.display_name, profile?.first_name, profile?.full_name, profile?.last_name, rxState.row, t],
  );
  const pendingSponsorRecovery = React.useMemo(() => {
    const authUserId = normalizeText(user?.id);
    if (!authUserId) return null;
    const pending = readPendingSponsorPayment(authUserId, "pharmacy_prescription_checkout");
    if (!pending || !normalizeText(pending.paymentLinkId)) return null;
    const matchesPrescription =
      normalizeText(pending.recoveryHint?.prescription_id || pending.prescriptionId) ===
      normalizeText(paymentView.prescriptionId);
    return matchesPrescription ? pending : null;
  }, [paymentView.prescriptionId, user?.id]);
  const sponsorSessionId = React.useMemo(
    () => normalizeText(sponsorReturnParams.get("session_id")),
    [sponsorReturnParams],
  );
  const isSponsorReturn = React.useMemo(
    () =>
      (normalizeText(sponsorReturnParams.get("sponsor_payment")) === "1" ||
        !!normalizeText(pendingSponsorRecovery?.paymentLinkId)) &&
      (!!sponsorSessionId || !!normalizeText(pendingSponsorRecovery?.paymentLinkId)),
    [pendingSponsorRecovery?.paymentLinkId, sponsorReturnParams, sponsorSessionId],
  );
  const sponsorRecoveryHint = React.useMemo(
    () => ({
      ...(pendingSponsorRecovery?.recoveryHint || {}),
      source: "pharmacy_prescription_checkout",
      sponsor_payment_lane: "pharmacy_prescription_checkout",
      prescription_id: paymentView.prescriptionId,
      appointment_id: normalizeText(rxState.row?.meta?.appointment_id || rxState.row?.appointment_id || ""),
      patient_user_id: normalizeText(user?.id),
      doctor_user_id: normalizeText(rxState.row?.meta?.doctor_user_id || rxState.row?.doctor_user_id || ""),
      pharmacy_owner_user_id: paymentView.pharmacyOwnerUserId,
      pharmacy_name: paymentView.pharmacyName,
      patient_name: paymentView.patientName,
      doctor_name: paymentView.doctorName,
      external_rx_id: paymentView.rxNo,
      medicine_name: paymentView.medicineName,
      fulfillment_mode: paymentView.fulfillmentMode,
      medicine_subtotal_cents: paymentView.medicineSubtotalCents,
      delivery_fee_cents:
        paymentView.fulfillmentMode === "delivery" ? paymentView.deliveryFeeCents : 0,
    }),
    [
      paymentView.deliveryFeeCents,
      paymentView.doctorName,
      paymentView.fulfillmentMode,
      paymentView.medicineName,
      paymentView.medicineSubtotalCents,
      paymentView.patientName,
      paymentView.pharmacyName,
      paymentView.pharmacyOwnerUserId,
      paymentView.prescriptionId,
      paymentView.rxNo,
      rxState.row?.appointment_id,
      rxState.row?.doctor_user_id,
      rxState.row?.meta?.appointment_id,
      rxState.row?.meta?.doctor_user_id,
      pendingSponsorRecovery?.recoveryHint,
      user?.id,
    ],
  );
  const checkoutStatusSummary = React.useMemo(
    () => buildCheckoutStatusSummary(paymentView, t),
    [paymentView, t],
  );

  async function handleCardSubmit({ stripe, elements, savedPaymentMethodId = "" }) {
    if (submitting) return;
    setSubmitting(true);
    setSubmitError("");

    try {
      const validatedSession = await resolveValidatedSession();
      const accessToken = normalizeText(validatedSession?.access_token);
      if (!accessToken) {
        throw new Error(t("prescriptions.checkout.errors.sessionExpired", "Session expired. Please sign in again."));
      }

      if (!paymentView.quoteReady || !paymentView.pharmacyOwnerUserId) {
        throw new Error(
          t(
            "prescriptions.checkout.errors.quoteNotReady",
            "This prescription does not have a pharmacy quote ready for payment yet.",
          ),
        );
      }

      const cleanSavedPaymentMethodId = normalizeText(savedPaymentMethodId);
      const cardElement = cleanSavedPaymentMethodId ? null : elements.getElement(CardElement);
      if (!cleanSavedPaymentMethodId && !cardElement) {
        throw new Error(
          t(
            "prescriptions.checkout.errors.formLoading",
            "Secure card form is still loading. Please refresh payment details.",
          ),
        );
      }

      const nextIntent = await createPharmacyPaymentIntent({
        prescription_id: paymentView.prescriptionId,
        pharmacy_owner_user_id: paymentView.pharmacyOwnerUserId,
        medicine_subtotal_cents: paymentView.medicineSubtotalCents,
        delivery_fee_cents: paymentView.fulfillmentMode === "delivery" ? paymentView.deliveryFeeCents : 0,
        currency: paymentView.currency,
        customer_email: normalizeText(user?.email),
        save_payment_method: rememberCard,
        accessToken,
        meta: {
          external_rx_id: paymentView.rxNo,
          medicine_name: paymentView.medicineName,
          patient_name: paymentView.patientName,
          doctor_name: paymentView.doctorName,
          pharmacy_name: paymentView.pharmacyName,
          fulfillment_mode: paymentView.fulfillmentMode,
        },
      });

      const clientSecret = normalizeText(nextIntent?.clientSecret);
      const nextPaymentIntentId = normalizeText(nextIntent?.paymentIntentId);
      const nextPlatformAccountId = normalizeText(nextIntent?.platformAccountId);
      const nextLivemode = typeof nextIntent?.livemode === "boolean" ? nextIntent.livemode : null;

      setIntentState({
        loading: false,
        error: "",
        livemode: nextLivemode,
        platformAccountId: nextPlatformAccountId,
      });

      if (!clientSecret) {
        throw new Error(
          t(
            "prescriptions.checkout.errors.secureCheckout",
            "Secure checkout could not be prepared cleanly. Refresh payment details and try again.",
          ),
        );
      }

      const lookupResult = await stripe.retrievePaymentIntent(clientSecret);
      if (lookupResult.error) {
        const lookupMessage = String(lookupResult.error.message || "").trim().toLowerCase();
        if (lookupMessage.includes("no such payment_intent")) {
          throw new Error(buildPaymentIntentMismatchMessage(nextPlatformAccountId, t));
        }
        throw new Error(
          lookupResult.error.message ||
            t("prescriptions.checkout.errors.prepareIntent", "Stripe could not prepare this payment intent."),
        );
      }

      const lookedUpPaymentIntentId = normalizeText(lookupResult.paymentIntent?.id);
      if (!lookedUpPaymentIntentId) {
        throw new Error(buildPaymentIntentMismatchMessage(nextPlatformAccountId, t));
      }

      if (nextPaymentIntentId && lookedUpPaymentIntentId !== nextPaymentIntentId) {
        throw new Error(buildPaymentIntentMismatchMessage(nextPlatformAccountId, t));
      }

      const result = await stripe.confirmCardPayment(
        clientSecret,
        cleanSavedPaymentMethodId
          ? { payment_method: cleanSavedPaymentMethodId }
          : {
              payment_method: {
                card: cardElement,
                billing_details: {
                  email: normalizeText(user?.email) || undefined,
                  name: paymentView.patientName || undefined,
                },
              },
            },
      );

      if (result.error) {
        throw new Error(
          result.error.message || t("prescriptions.checkout.errors.paymentFailed", "Payment could not be completed."),
        );
      }

      const paymentStatus = normalizeText(result?.paymentIntent?.status).toLowerCase();
      if (paymentStatus && paymentStatus !== "succeeded") {
        throw new Error(
          t("prescriptions.checkout.errors.processing", "Your payment is still processing. Please wait a moment and try again."),
        );
      }

      const paymentIntentId = normalizeText(result?.paymentIntent?.id || nextPaymentIntentId);
      if (!paymentIntentId) {
        throw new Error(
          t("prescriptions.checkout.errors.referenceMissing", "Payment completed but the payment reference was missing."),
        );
      }

      await syncPharmacyPayment({
        prescription_id: paymentView.prescriptionId,
        payment_intent_id: paymentIntentId,
        accessToken,
      });

      window.dispatchEvent(new Event("afrimed:transactions:changed"));
      nav(`/appointments/prescriptions/${paymentView.prescriptionId}`, {
        replace: true,
      });
    } catch (error) {
      setSubmitError(formatCheckoutError(error, t));
    } finally {
      setSubmitting(false);
    }
  }

  async function handlePaystackSubmit() {
    if (submitting) return;
    if (paymentView.deliveryQuoteIncomplete) {
      setSubmitError(t("prescriptions.checkout.state.deliveryQuoteIncompleteHelper", "The pharmacy is still finalising your delivery quote."));
      return;
    }
    setSubmitting(true);
    setSubmitError("");
    try {
      const session = await resolveValidatedSession();
      const accessToken = normalizeText(session?.access_token);
      if (!accessToken) throw new Error("Session expired. Please sign in again.");
      const result = await initializePaystackPharmacyPayment({
        prescription_id: paymentView.prescriptionId,
        pharmacy_owner_user_id: paymentView.pharmacyOwnerUserId,
        medicine_subtotal_cents: paymentView.medicineSubtotalCents,
        delivery_fee_cents: paymentView.fulfillmentMode === "delivery" ? paymentView.deliveryFeeCents : 0,
        currency: paymentView.currency,
        customer_email: normalizeText(user?.email),
        callback_url: `${window.location.origin}/appointments/prescriptions/${paymentView.prescriptionId}/pay`,
        accessToken,
        meta: { external_rx_id: paymentView.rxNo, fulfillment_mode: paymentView.fulfillmentMode },
      });
      const accessCode = normalizeText(result?.accessCode);
      if (!accessCode) throw new Error("Secure payment could not be prepared.");
      await openPaystackInlineCheckout(accessCode);
      setSubmitting(false);
      const reference = normalizeText(result?.reference);
      if (!reference) return;
      void (async () => {
        for (let attempt = 0; attempt < 45; attempt += 1) {
          await new Promise((resolve) => window.setTimeout(resolve, 2000));
          try {
            const freshSession = await resolveValidatedSession();
            await verifyPaystackPharmacyPayment({
              prescription_id: paymentView.prescriptionId,
              reference,
              accessToken: normalizeText(freshSession?.access_token),
            });
            window.dispatchEvent(new Event("afrimed:transactions:changed"));
            nav(`/appointments/prescriptions/${paymentView.prescriptionId}`, { replace: true });
            return;
          } catch (verifyError) {
            if (attempt === 44) {
              if (import.meta.env.DEV) console.error("pharmacy_paystack_popup_verify_failed", verifyError);
              setSubmitError("We couldn’t confirm your secure payment. Please refresh this page.");
            }
          }
        }
      })();
    } catch (error) {
      if (import.meta.env.DEV) console.error("pharmacy_paystack_initialize_failed", error);
      setSubmitError(formatCheckoutError(error, t));
      setSubmitting(false);
    }
  }

  React.useEffect(() => {
    const reference = normalizeText(new URLSearchParams(location.search).get("reference"));
    if (!isPaystackProvider || !reference || !paymentView.prescriptionId || paymentView.paymentStatus === "paid") return;
    let alive = true;
    async function verify() {
      setSubmitting(true);
      setSubmitError("");
      try {
        const session = await resolveValidatedSession();
        await verifyPaystackPharmacyPayment({
          prescription_id: paymentView.prescriptionId,
          reference,
          accessToken: normalizeText(session?.access_token),
        });
        if (alive) {
          window.dispatchEvent(new Event("afrimed:transactions:changed"));
          nav(`/appointments/prescriptions/${paymentView.prescriptionId}`, { replace: true });
        }
      } catch (error) {
        if (alive) setSubmitError(formatCheckoutError(error, t));
      } finally {
        if (alive) setSubmitting(false);
      }
    }
    void verify();
    return () => { alive = false; };
  }, [isPaystackProvider, location.search, nav, paymentView.paymentStatus, paymentView.prescriptionId, t]);

  React.useEffect(() => {
    let alive = true;

    async function syncSponsorReturn() {
      const paymentLinkId = normalizeText(pendingSponsorRecovery?.paymentLinkId);
      if (!isSponsorReturn || (!sponsorSessionId && !paymentLinkId)) {
        if (alive) setSponsorReturnState({ loading: false, error: "" });
        return;
      }

      setSponsorReturnState({ loading: true, error: "" });

      try {
        const syncPayload = sponsorSessionId
          ? { session_id: sponsorSessionId, recovery_hint: sponsorRecoveryHint }
          : { payment_link_id: paymentLinkId, recovery_hint: sponsorRecoveryHint };
        await syncSponsorPaymentSession(syncPayload);
        if (!alive) return;
        clearPendingSponsorPayment(normalizeText(user?.id), "pharmacy_prescription_checkout");
        window.dispatchEvent(new Event("afrimed:transactions:changed"));
        nav(`/appointments/prescriptions/${paymentView.prescriptionId}`, { replace: true });
      } catch (error) {
        if (!alive) return;
        setSponsorReturnState({
          loading: false,
          error:
            error?.message ||
            t(
              "prescriptions.checkout.errors.sponsorRestore",
              "Sponsor payment completed, but AfriMed™ could not restore the pharmacy payment yet.",
            ),
        });
      }
    }

    void syncSponsorReturn();
    return () => {
      alive = false;
    };
  }, [
    isSponsorReturn,
    nav,
    paymentView.prescriptionId,
    pendingSponsorRecovery?.paymentLinkId,
    sponsorRecoveryHint,
    sponsorSessionId,
    user?.id,
  ]);

  function handlePrepareSponsorPayment() {
    if (paymentView.deliveryQuoteIncomplete) {
      setSubmitError(t("prescriptions.checkout.state.deliveryQuoteIncompleteHelper", "The pharmacy is still finalising your delivery quote."));
      return;
    }
    const prescriptionId = normalizeText(paymentView.prescriptionId);
    if (!prescriptionId) return;
    const origin = String(window.location.origin || "").replace(/\/+$/, "");
    nav("/settings/patient/preferences/transactions/prepare-sponsor-payment", {
      state: {
        source: "pharmacy_prescription_checkout",
        returnTo: `/appointments/prescriptions/${prescriptionId}/pay`,
        prefill: {
          paymentFor: paymentView.pharmacyName
            ? `${paymentView.pharmacyName} prescription fulfilment`
            : t("prescriptions.checkout.sponsor.prefill", "Prescription fulfilment"),
          amount: (paymentView.totalCents / 100).toFixed(2),
          currency: paymentView.currency,
        },
        sellerRole: "pharmacy",
        sellerUserId: paymentView.pharmacyOwnerUserId,
        sellerName: paymentView.pharmacyName,
        pharmacyOwnerUserId: paymentView.pharmacyOwnerUserId,
        prescriptionId,
        successUrl: `${origin}/appointments/prescriptions/${prescriptionId}/pay?sponsor_payment=1&session_id={CHECKOUT_SESSION_ID}`,
        cancelUrl: `${origin}/appointments/prescriptions/${prescriptionId}/pay`,
        metadata: sponsorRecoveryHint,
      },
    });
  }

  function openSupportThread() {
    const conversationId = normalizeText(paymentView.supportConversationId);
    if (!conversationId) return;
    nav(`/messages/${conversationId}`, {
      state: {
        conversationTitle: t("prescriptions.checkout.openFolaShort", "Fola"),
        conversationType: "fola",
        returnTo: location.pathname,
        returnToState: location.state || null,
      },
    });
  }

  const content = (() => {
    if (rxState.loading) {
      return <StatusCard>{t("prescriptions.checkout.status.preparing", "Preparing pharmacy checkout…")}</StatusCard>;
    }

    if (rxState.error) {
      return <StatusCard tone="critical">{rxState.error}</StatusCard>;
    }

    if (isSponsorReturn && sponsorReturnState.loading) {
      return (
        <StatusCard>
          {t(
            "prescriptions.checkout.status.restoringSponsor",
            "Restoring sponsor payment and syncing the pharmacy ledger…",
          )}
        </StatusCard>
      );
    }

    if (isSponsorReturn && sponsorReturnState.error) {
      return <StatusCard tone="critical">{sponsorReturnState.error}</StatusCard>;
    }

    if (paymentView.deliveryQuoteIncomplete) {
      return (
        <StatusCard>
          {t("prescriptions.checkout.state.deliveryQuoteIncompleteHelper", "The pharmacy is still finalising your delivery quote.")}
        </StatusCard>
      );
    }

    if (!paymentView.quoteReady) {
      return (
        <StatusCard>
          {t(
            "prescriptions.checkout.status.awaitingQuoteBody",
            "The pharmacy has not prepared a payable quote for this prescription yet. Open the prescription details again after the pharmacy confirms subtotal and fulfilment.",
          )}
        </StatusCard>
      );
    }

    if (paymentView.paymentStatus === "paid") {
      return (
        <StatusCard tone="success">
          {t(
            "prescriptions.checkout.status.alreadyPaid",
            "This prescription is already paid. You can return to the prescription details for the current payment and refund status.",
          )}
        </StatusCard>
      );
    }

    if (paymentView.paymentStatus === "refund_pending" || paymentView.refundStatus === "pending") {
      return (
        <StatusCard>
          {t(
            "prescriptions.checkout.status.alreadyReversing",
            "This pharmacy payment is already being reversed. Return to the prescription details for the current refund status.",
          )}
        </StatusCard>
      );
    }

    if (paymentView.paymentStatus === "refunded" || paymentView.refundStatus === "succeeded") {
      return <StatusCard>{t("prescriptions.checkout.status.alreadyRefunded", "This pharmacy payment has already been refunded.")}</StatusCard>;
    }

    if (paymentProviderMode === "unsupported") {
      return <StatusCard tone="critical">Secure payment is unavailable because no supported payment provider is configured.</StatusCard>;
    }

    if (isPaystackProvider) {
      return (
        <PaystackTestCheckout
          onPay={handlePaystackSubmit}
          onPrepareSponsorPayment={handlePrepareSponsorPayment}
          submitting={submitting}
          error={submitError}
          showTestHelper={isPaystackTest && import.meta.env.DEV}
        />
      );
    }

    if (isWorldlineProvider) {
      return (
        <StatusCard>
          This payment route is available only when Worldline is explicitly enabled for this transaction.
        </StatusCard>
      );
    }

    return <StatusCard tone="critical">Secure payment is unavailable for the configured payment provider.</StatusCard>;
  })();

  return (
    <div className="min-h-[100dvh] bg-[rgb(var(--clr-surface-container-low))]">
      <div className="afrimed-rail afrimed-rail--comfortable pt-4 pb-24">
        <TopBarCenter
          title={t("prescriptions.checkout.title", "Pharmacy checkout")}
          close
          onBack={() => nav(`/appointments/prescriptions/${id}`)}
        />

        <Card className="mt-6 p-5">
          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            <CheckoutSummaryItem
              label={t("prescriptions.checkout.summary.prescription", "Prescription")}
              value={paymentView.rxNo}
              helper={paymentView.medicineName}
            />
            <CheckoutSummaryItem
              label={t("prescriptions.checkout.summary.pharmacy", "Dispensing pharmacy")}
              value={paymentView.pharmacyName || t("prescriptions.checkout.summary.awaitingBranch", "Awaiting branch")}
              helper={
                paymentView.fulfillmentMode === "delivery"
                  ? t("prescriptions.checkout.summary.deliveryHelper", "Delivery quote prepared by the pharmacy.")
                  : t("prescriptions.checkout.summary.pickupHelper", "Pickup handoff prepared by the pharmacy.")
              }
            />
            <CheckoutSummaryItem
              label={t("prescriptions.checkout.summary.subtotal", "Medicine subtotal")}
              value={formatCurrencyFromCents(paymentView.medicineSubtotalCents, paymentView.currency)}
            />
            <CheckoutSummaryItem
              label={t("prescriptions.checkout.summary.delivery", "Delivery")}
              value={paymentView.deliveryQuoteIncomplete ? t("prescriptions.checkout.summary.incomplete", "Incomplete") : formatCurrencyFromCents(paymentView.deliveryFeeCents, paymentView.currency)}
            />
            <CheckoutSummaryItem
              label={t("prescriptions.checkout.summary.total", "Total")}
              value={paymentView.deliveryQuoteIncomplete ? t("prescriptions.checkout.summary.incomplete", "Incomplete") : formatCurrencyFromCents(paymentView.totalCents, paymentView.currency)}
              helper={t(
                "prescriptions.checkout.summary.totalHelper",
                "AfriMed™ platform fee is handled behind the scenes and not added on top of this patient total.",
              )}
            />
            <CheckoutSummaryItem
              label={t("prescriptions.checkout.summary.state", "Fulfillment state")}
              value={checkoutStatusSummary.label}
              helper={checkoutStatusSummary.helper}
            />
            <CheckoutSummaryItem
              label={t("prescriptions.checkout.summary.mode", "Mode")}
              value={formatFulfillmentModeLabel(paymentView.fulfillmentMode, t)}
              helper={t(
                "prescriptions.checkout.summary.modeHelper",
                "Use the Fola handoff if the pharmacy needs clarification before release.",
              )}
            />
          </div>
          <div className="mt-5 grid gap-3 border-t border-[rgb(var(--clr-outline-variant))] pt-5 sm:flex sm:flex-wrap">
              {paymentView.supportConversationId ? (
                <Button type="button" variant="tonal" className="h-12" onClick={openSupportThread}>
                  {t("prescriptions.checkout.openFola", "Open Fola handoff")}
                </Button>
              ) : null}
              <ContextFeedbackLauncher
                section="prescription-checkout"
                roleOverride="patient"
                entity={{
                  id: paymentView.prescriptionId,
                  label: paymentView.rxNo || paymentView.medicineName || t("prescriptions.common.item", "prescription"),
                  type: "prescription",
                }}
                context={{
                  pharmacyName: paymentView.pharmacyName,
                  paymentStatus: paymentView.paymentStatus,
                  fulfillmentMode: paymentView.fulfillmentMode,
                }}
                subtitle={t(
                  "prescriptions.checkout.feedbackSubtitle",
                  "Capture this checkout step so AfriMed™ can reopen the exact tester context.",
                )}
              />
          </div>
        </Card>
        <section className="mt-6 min-w-0">{content}</section>
      </div>
    </div>
  );
}
